/**
 * The offline write queue's replay engine (`docs/OFFLINE.md` §2).
 *
 * `enqueueWrite()` appends a request to `src/offline/queue.ts`'s storage;
 * `flush()` sends whatever is `pending`, **one entry at a time, in order**:
 * the first network failure or `5xx`/`429` stops the pass (backoff,
 * `attempts × 15s` capped at 5 minutes, then a fresh pass); a `4xx` other
 * than `429` is final — the entry becomes `rejected` and the pass moves on
 * to the next one. A replayed body always carries the same `client_id`, so a
 * lost acknowledgement never records the same write twice (`docs/API.md`
 * §6-§8): the server answers `200` instead of `201` on replay, and both are
 * treated as success here.
 *
 * Triggers: `online`, app start, right after a successful write (called
 * explicitly by `src/api/household.ts`), and every 60s while entries are
 * pending — {@link startOfflineSync} wires the first, third and last up
 * once, from `AppShell`.
 */
import { ApiError, apiFetch } from '../api/client';

import { enqueue, list, remove, setLastSyncAt, update } from './queue';

import type { ApiErrorBody } from '../api/types';
import type { QueueEntry, QueueEntryInput } from './queue';

/** Backoff step: `attempts × 15s`, capped at 5 minutes (`docs/OFFLINE.md` §2). */
const BACKOFF_STEP_MS = 15_000;
const BACKOFF_MAX_MS = 5 * 60_000;
/** How often `flush()` is retried in the background while entries are pending. */
const PERIODIC_FLUSH_MS = 60_000;

/** One entry the current pass sent successfully. */
export interface SentWrite {
  clientId: string;
  /** The parsed JSON response body (the server's `200`/`201` payload). */
  response: unknown;
}

/** One entry the current pass gave up on: a final `4xx` other than `429`. */
export interface RejectedWrite {
  clientId: string;
  status: number;
  code?: string;
  detail: string;
}

/** What one `flush()` pass did. */
export interface FlushSummary {
  sent: SentWrite[];
  rejected: RejectedWrite[];
  /** `true` when the pass stopped early on a network error, `5xx` or `429`. */
  stoppedOnError: boolean;
}

/**
 * Mint a fresh idempotency key for a queued write (`docs/OFFLINE.md` §3):
 * `crypto.randomUUID()`, embedded in the request body as `client_id` and
 * kept alongside the queue entry so a replay is recognised as the same
 * write.
 *
 * @returns A UUID v4 string.
 */
export function newClientId(): string {
  return crypto.randomUUID();
}

/**
 * Append a write to the offline queue. A thin, explicitly-named wrapper
 * around `queue.ts`'s `enqueue()` (`docs/OFFLINE.md` §2's own naming for
 * this module).
 *
 * @param input - Method, path, body (`client_id` already embedded) and the
 *   same `client_id` again, so `flush()` can match a response back to it
 *   without re-parsing the body.
 * @returns The persisted, `pending` entry.
 */
export function enqueueWrite(input: QueueEntryInput): Promise<QueueEntry> {
  return enqueue(input);
}

let flushing: Promise<FlushSummary> | undefined;
let retryTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * Whether `error` should stop the pass and retry later: a network failure
 * (not an {@link ApiError} at all — `fetch` itself rejected), a `5xx`, or a
 * `429`. Anything else is a final `4xx`.
 */
function isRetryable(error: unknown): boolean {
  if (!(error instanceof ApiError)) return true;
  return error.status === 429 || error.status >= 500;
}

/**
 * The backoff delay before the next pass after a failed attempt:
 * `attempts × 15s`, capped at 5 minutes (`docs/OFFLINE.md` §2). Exported as
 * a pure function so it can be tested directly, without driving real or
 * faked timers through a whole retry cycle.
 *
 * @param attempts - How many times this entry has been tried, this one included.
 * @returns The delay, in milliseconds, before the next pass.
 */
export function computeBackoffDelay(attempts: number): number {
  return Math.min(attempts * BACKOFF_STEP_MS, BACKOFF_MAX_MS);
}

/** Schedule the next retry pass after a failure, replacing any pending one. */
function scheduleRetry(attempts: number): void {
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = setTimeout(() => {
    retryTimer = undefined;
    void flush();
  }, computeBackoffDelay(attempts));
}

/**
 * Send whatever is `pending`, oldest first, one at a time. Never called
 * concurrently with itself — see {@link flush}.
 */
async function runFlush(): Promise<FlushSummary> {
  const summary: FlushSummary = { sent: [], rejected: [], stoppedOnError: false };
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return summary;
  }

  for (;;) {
    const entries = await list();
    const next = entries
      .filter((entry) => entry.status === 'pending')
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt))[0];
    if (!next) break;

    await update(next.id, { status: 'sending' });
    try {
      const response = await apiFetch<unknown>(next.path, {
        method: next.method,
        body: JSON.stringify(next.body),
      });
      await remove(next.id);
      summary.sent.push({ clientId: next.clientId, response });
      await setLastSyncAt(new Date().toISOString());
    } catch (error) {
      const attempts = next.attempts + 1;
      if (error instanceof ApiError && !isRetryable(error)) {
        const body = error.body as ApiErrorBody | undefined;
        const detail = body?.detail ?? error.message;
        await update(next.id, {
          status: 'rejected',
          attempts,
          ...(body?.code ? { lastError: { code: body.code, detail } } : { lastError: { detail } }),
        });
        summary.rejected.push({
          clientId: next.clientId,
          status: error.status,
          ...(body?.code ? { code: body.code } : {}),
          detail,
        });
        continue;
      }
      // Network error, `5xx` or `429`: back off and stop this whole pass so
      // later entries never jump ahead of one still waiting to be retried.
      await update(next.id, { status: 'pending', attempts });
      summary.stoppedOnError = true;
      scheduleRetry(attempts);
      break;
    }
  }
  return summary;
}

/**
 * Run one replay pass over the queue, or await the pass already in flight.
 * Safe to call as often as needed (`online`, app start, after a write,
 * every 60s): concurrent calls share a single pass.
 *
 * @returns What this pass (or the one it joined) did.
 */
export function flush(): Promise<FlushSummary> {
  flushing ??= runFlush().finally(() => {
    flushing = undefined;
  });
  return flushing;
}

let started = false;
let onlineListener: (() => void) | undefined;
let periodicTimer: ReturnType<typeof setInterval> | undefined;

/**
 * Wire the background triggers of `docs/OFFLINE.md` §2: an immediate pass,
 * one on every `online` event, and one every 60s while entries are pending.
 * Idempotent — a second call is a no-op — so every mount of `AppShell` can
 * call it safely.
 */
export function startOfflineSync(): void {
  if (started) return;
  started = true;

  onlineListener = () => void flush();
  window.addEventListener('online', onlineListener);

  periodicTimer = setInterval(() => {
    void list().then((entries) => {
      if (entries.some((entry) => entry.status === 'pending' || entry.status === 'sending')) {
        void flush();
      }
    });
  }, PERIODIC_FLUSH_MS);

  void flush();
}

/**
 * Tear down everything {@link startOfflineSync} set up and reset the
 * in-flight/backoff state. Not used at runtime — the app never stops
 * syncing while it's open — but tests call it in `afterEach` so a scheduled
 * retry from one test can never fire during the next one.
 */
export function resetOfflineSyncForTests(): void {
  started = false;
  flushing = undefined;
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = undefined;
  }
  if (periodicTimer) {
    clearInterval(periodicTimer);
    periodicTimer = undefined;
  }
  if (onlineListener) {
    window.removeEventListener('online', onlineListener);
    onlineListener = undefined;
  }
}
